import React from 'react';
import { Clock, AlertCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface GlassTimePickerProps {
    durationHours: number;
    onChange: (totalHours: number) => void;
    title?: string;
    showWarning?: boolean;
}

export const GlassTimePicker: React.FC<GlassTimePickerProps> = ({
    durationHours,
    onChange,
    title = 'Offer Duration',
    showWarning = true,
}) => {
    const { impactOccurred } = useHapticFeedback();

    const days = Math.floor(durationHours / 24);
    const hours = Math.floor(durationHours % 24);
    const minutes = Math.round((durationHours % 1) * 60);

    const updateTime = (d: number, h: number, m: number) => {
        const clampedD = Math.max(0, Math.min(30, d));
        const clampedH = Math.max(0, Math.min(23, h));
        const clampedM = Math.max(0, Math.min(59, m));

        let total = clampedD * 24 + clampedH + clampedM / 60;
        if (total < 0.166667) total = 0.166667; // Minimum 10 minutes
        onChange(total);
    };

    const applyPreset = (presetHours: number) => {
        impactOccurred('light');
        const d = Math.floor(presetHours / 24);
        const h = Math.floor(presetHours % 24);
        const m = Math.round((presetHours % 1) * 60);
        updateTime(d, h, m);
    };

    return (
        <div className="space-y-3 p-4 rounded-[24px] pfk-glass border border-white/10 shadow-xl text-left">
            <style>{`
                .pfk-glass {
                  background-color: rgba(28,28,30,.65);
                  -webkit-backdrop-filter: blur(12px) saturate(160%);
                  backdrop-filter: blur(12px) saturate(160%);
                  box-shadow: inset 0 0 0 1px rgba(255,255,255,.08), inset 1.8px 3px 0px -2px rgba(255,255,255,.27), inset -2px -2px 0px -2px rgba(255,255,255,.22), inset -3px -8px 1px -6px rgba(255,255,255,.16), inset -.3px -1px 4px 0px rgba(0,0,0,.24), inset -1.5px 2.5px 0px -2px rgba(0,0,0,.4), 0px 4px 20px 0px rgba(0,0,0,.25);
                }
                .pfk-wheel-item {
                  background-color: rgba(28,28,30,.45);
                  -webkit-backdrop-filter: blur(8px) saturate(140%);
                  backdrop-filter: blur(8px) saturate(140%);
                  box-shadow: inset 0 0 0 1px rgba(255,255,255,.06), inset 1.8px 3px 0px -2px rgba(255,255,255,.25), 0px 2px 8px 0px rgba(0,0,0,.3);
                }
                .pfk-pill-active {
                  background: linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(168,85,247,0.9) 100%);
                  box-shadow: 0 4px 14px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.3);
                }
            `}</style>

            {/* Header */}
            <div className="flex items-center justify-between">
                <label className="text-xs font-extrabold uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
                    <Clock size={14} className="text-purple-400" />
                    <span>{title}</span>
                </label>
                <div className="flex items-center gap-1 text-[11px] font-mono font-extrabold text-purple-200 bg-purple-500/20 px-2.5 py-1 rounded-full border border-purple-500/30">
                    {days > 0 && <span>{days}d </span>}
                    {hours > 0 && <span>{hours}h </span>}
                    <span>{minutes}m</span>
                </div>
            </div>

            {/* Stepper Columns: Days, Hours, Minutes */}
            <div className="grid grid-cols-3 gap-2.5 pt-1">
                {/* Days Column */}
                <div className="flex flex-col items-center pfk-wheel-item rounded-2xl p-2 border border-white/10">
                    <span className="text-[10px] font-extrabold text-white/50 uppercase tracking-wider mb-1">DAYS</span>
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days + 1, hours, minutes); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronUp size={18} />
                    </button>
                    <input
                        type="number"
                        min="0"
                        max="30"
                        value={days}
                        onChange={(e) => updateTime(parseInt(e.target.value) || 0, hours, minutes)}
                        className="w-full bg-transparent text-center text-xl font-black text-white outline-none font-mono py-1"
                    />
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days - 1, hours, minutes); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronDown size={18} />
                    </button>
                </div>

                {/* Hours Column */}
                <div className="flex flex-col items-center pfk-wheel-item rounded-2xl p-2 border border-white/10">
                    <span className="text-[10px] font-extrabold text-white/50 uppercase tracking-wider mb-1">HOURS</span>
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days, hours + 1, minutes); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronUp size={18} />
                    </button>
                    <input
                        type="number"
                        min="0"
                        max="23"
                        value={hours}
                        onChange={(e) => updateTime(days, parseInt(e.target.value) || 0, minutes)}
                        className="w-full bg-transparent text-center text-xl font-black text-white outline-none font-mono py-1"
                    />
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days, hours - 1, minutes); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronDown size={18} />
                    </button>
                </div>

                {/* Minutes Column */}
                <div className="flex flex-col items-center pfk-wheel-item rounded-2xl p-2 border border-white/10">
                    <span className="text-[10px] font-extrabold text-white/50 uppercase tracking-wider mb-1">MINS</span>
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days, hours, minutes + 5); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronUp size={18} />
                    </button>
                    <input
                        type="number"
                        min="0"
                        max="59"
                        value={minutes}
                        onChange={(e) => updateTime(days, hours, parseInt(e.target.value) || 0)}
                        className="w-full bg-transparent text-center text-xl font-black text-white outline-none font-mono py-1"
                    />
                    <button
                        type="button"
                        onClick={() => { impactOccurred('light'); updateTime(days, hours, minutes - 5); }}
                        className="w-full py-1 text-purple-300 hover:text-white flex justify-center active:scale-90 transition-transform"
                    >
                        <ChevronDown size={18} />
                    </button>
                </div>
            </div>

            {/* Quick Presets Bar */}
            <div className="flex items-center gap-1.5 pt-1 overflow-x-auto no-scrollbar">
                {[
                    { label: '10m', h: 0.166667 },
                    { label: '1h', h: 1 },
                    { label: '6h', h: 6 },
                    { label: '12h', h: 12 },
                    { label: '24h (1d)', h: 24 },
                    { label: '3d', h: 72 },
                    { label: '7d', h: 168 },
                    { label: '30d', h: 720 },
                ].map((p) => {
                    const isSelected = Math.abs((days * 24 + hours + minutes / 60) - p.h) < 0.05;
                    return (
                        <button
                            key={p.label}
                            type="button"
                            onClick={() => applyPreset(p.h)}
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all shrink-0 active:scale-95 ${
                                isSelected
                                    ? 'pfk-pill-active text-white border border-purple-300/40'
                                    : 'pfk-wheel-item text-white/60 hover:text-white border border-white/5'
                            }`}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>

            {/* Warning Glass Banner */}
            {showWarning && (
                <div className="mt-3 p-3.5 rounded-2xl bg-amber-500/15 border border-amber-500/30 backdrop-blur-md flex items-start gap-3 text-left">
                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-0.5">
                        <span className="font-extrabold text-[11px] text-amber-300">Offer Funds Reservation Notice:</span>
                        <p className="text-[10.5px] font-medium text-amber-200/90 leading-relaxed">
                            Offer funds will be frozen until the specified duration completes. If the user does not accept the offer before time expires, your funds will be automatically refunded to your wallet.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
